import { Link } from 'react-router-dom';
import StatusBadge from '@/components/base/StatusBadge';
import { categoryMap } from '@/mocks/categories';
import type { Topic } from '@/mocks/topics';

interface TopicCardProps {
  topic: Topic;
  variant?: 'default' | 'compact';
}

export default function TopicCard({ topic, variant = 'default' }: TopicCardProps) {
  const category = categoryMap[topic.categoryId];

  if (variant === 'compact') {
    return (
      <article className="group flex gap-4 border-b border-background-200 py-5 last:border-b-0">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={topic.status} withShort />
            <span className="font-label text-xs text-foreground-500">{category?.name}</span>
          </div>
          <h3 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
            <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
              {topic.title}
            </Link>
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-3 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {topic.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {topic.heat.toLocaleString()}
            </span>
          </div>
        </div>
        <Link
          to={`/topic/${topic.id}`}
          className="hidden h-20 w-28 flex-none overflow-hidden rounded-lg sm:block"
          aria-label={topic.title}
        >
          <img src={topic.cover} alt={topic.coverAlt} className="h-full w-full object-cover object-top" />
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/topic/${topic.id}`} className="block h-44 w-full overflow-hidden sm:h-48" aria-label={topic.title}>
        <img
          src={topic.cover}
          alt={topic.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={topic.status} />
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-800">
            {category?.name}
          </span>
        </div>
        <h3 className="mt-3 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-700">{topic.summary}</p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 font-label text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line"></i>
            {topic.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {topic.heat.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-file-list-2-line"></i>
              {topic.evidence.length}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}